import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useClients } from '@/modules/commercial/hooks';
import { getCommercialOperation } from '@/modules/commercial/services';
import { getInvoice, saveInvoice } from '@/modules/billing/services';
import {
  EDITABLE_INVOICE_STATUSES,
  INVOICE_STATUS_LABELS,
  INVOICE_TYPE_LABELS,
  type InvoiceInput,
  type InvoiceStatus,
  type InvoiceType,
} from '@/modules/billing/types';
import { parseInvoicePrefill } from '@/modules/billing/utils/billingUrl';
import { buildInvoiceInputFromOperation } from '@/modules/billing/utils/invoiceFromOperation';
import { validateInvoiceInput } from '@/modules/billing/utils/validation';
import { modulePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';

const defaultInput: InvoiceInput = {
  commercialOperationId: null,
  clientId: null,
  type: 'internal',
  status: 'draft',
  issueDate: new Date().toISOString().slice(0, 10),
  dueDate: '',
  subtotal: 0,
  taxes: 0,
  total: 0,
  currency: 'ARS',
  notes: '',
};

export function InvoiceFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { profile, hasPermission } = useAuth();
  const canWrite = hasPermission('billing.write');
  const { clients } = useClients();

  const [form, setForm] = useState<InvoiceInput>(defaultInput);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isNew) return;
    const prefill = parseInvoicePrefill(searchParams);
    setForm((prev) => ({
      ...prev,
      ...(prefill.commercialOperationId
        ? { commercialOperationId: prefill.commercialOperationId }
        : {}),
      ...(prefill.clientId ? { clientId: prefill.clientId } : {}),
    }));
  }, [isNew, searchParams]);

  useEffect(() => {
    if (!isNew) return;
    const prefill = parseInvoicePrefill(searchParams);
    if (!prefill.commercialOperationId) return;
    void getCommercialOperation(prefill.commercialOperationId).then((op) => {
      if (op) setForm(buildInvoiceInputFromOperation(op));
    });
  }, [isNew, searchParams]);

  useEffect(() => {
    if (isNew || !id) return;
    setLoading(true);
    void getInvoice(id)
      .then((inv) => {
        if (!inv) {
          setError('Factura no encontrada.');
          return;
        }
        if (inv.status !== 'draft') {
          setError('Solo se pueden editar facturas en borrador.');
        }
        setForm({
          commercialOperationId: inv.commercialOperationId,
          clientId: inv.clientId,
          type: inv.type,
          status: inv.status,
          issueDate: inv.issueDate,
          dueDate: inv.dueDate,
          subtotal: inv.subtotal,
          taxes: inv.taxes,
          total: inv.total,
          currency: inv.currency,
          notes: inv.notes,
        });
      })
      .catch(() => setError('Error al cargar la factura.'))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canWrite || !profile) return;
    const validation = validateInvoiceInput(form);
    if (validation) {
      setError(validation);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await saveInvoice(
        form,
        profile.uid,
        isNew ? undefined : id,
      );
      void navigate(`${modulePaths.billing}/facturas/${saved.id}`, {
        replace: true,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'No se pudo guardar la factura.',
      );
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="auth-muted">Cargando…</p>;

  return (
    <section>
      <p>
        <Link to={`${modulePaths.billing}/facturas`}>← Facturas</Link>
      </p>
      <h1>{isNew ? 'Nueva factura interna' : 'Editar factura'}</h1>

      {!canWrite ? (
        <p className="auth-error">No tenés permiso para editar facturas.</p>
      ) : (
        <form className="inventory-panel" onSubmit={handleSubmit}>
          <div className="users-form-grid">
            <label className="auth-label">
              Tipo
              <select
                value={form.type}
                onChange={(e) =>
                  setForm({ ...form, type: e.target.value as InvoiceType })
                }
              >
                <option value="internal">{INVOICE_TYPE_LABELS.internal}</option>
                <option value="fiscal" disabled>
                  {INVOICE_TYPE_LABELS.fiscal} (futuro ARCA)
                </option>
              </select>
            </label>
            <label className="auth-label">
              Estado
              <select
                value={form.status}
                onChange={(e) =>
                  setForm({ ...form, status: e.target.value as InvoiceStatus })
                }
              >
                {EDITABLE_INVOICE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {INVOICE_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </label>
            <label className="auth-label">
              Cliente
              <select
                value={form.clientId ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    clientId: e.target.value || null,
                  })
                }
              >
                <option value="">— Sin cliente —</option>
                {clients
                  .filter((c) => c.active)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </label>
            <label className="auth-label">
              ID operación comercial
              <input
                value={form.commercialOperationId ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    commercialOperationId: e.target.value || null,
                  })
                }
                placeholder="Opcional"
              />
            </label>
          </div>

          <div className="users-form-grid">
            <label className="auth-label">
              Fecha emisión *
              <input
                type="date"
                required
                value={form.issueDate}
                onChange={(e) =>
                  setForm({ ...form, issueDate: e.target.value })
                }
              />
            </label>
            <label className="auth-label">
              Vencimiento
              <input
                type="date"
                value={form.dueDate}
                onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
              />
            </label>
            <label className="auth-label">
              Moneda
              <input
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
              />
            </label>
          </div>

          <div className="users-form-grid">
            <label className="auth-label">
              Subtotal
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.subtotal || ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    subtotal: Number(e.target.value) || 0,
                  })
                }
              />
            </label>
            <label className="auth-label">
              Impuestos
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.taxes || ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    taxes: Number(e.target.value) || 0,
                  })
                }
              />
            </label>
            <label className="auth-label">
              Total
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.total || ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    total: Number(e.target.value) || 0,
                  })
                }
              />
            </label>
          </div>

          <label className="auth-label">
            Notas
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </label>

          <p className="auth-muted">
            La numeración interna (INV-000001) se asigna al pasar a estado
            emitida o pendiente.
          </p>

          {error ? <p className="auth-error">{error}</p> : null}
          <div className="inventory-actions">
            <button type="submit" className="auth-button" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
            <Link
              to={`${modulePaths.billing}/facturas`}
              className="auth-button auth-button--secondary"
            >
              Cancelar
            </Link>
          </div>
        </form>
      )}
    </section>
  );
}
